import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Plus,
  Settings,
  BookOpen,
  FileText,
  Tag,
  Edit,
  Eye,
  CheckCircle,
  AlertCircle,
  Search,
  X,
  TrendingUp,
  FolderOpen,
  ExternalLink,
  MoreHorizontal,
} from 'lucide-react';
import { cn } from '../../utils/cn';
import { getPublishedSiteUrl } from '../../utils/publishedUrl';
import { Tooltip } from '../../components/Tooltip';
import { DeleteConfirmModal } from '../../components/DeleteConfirmModal';
import { BlogSEOOverviewPanel } from '../../components/BlogSEOOverviewPanel';
import { useBlogManagerState } from './useBlogManagerState';
import { createBlogManagerHandlers } from './useBlogManagerHandlers';
import { BlogSettingsPanel } from './BlogSettingsPanel';
import { BlogPostsList } from './BlogPostsList';
import { MediaLibraryModal } from '../../components/MediaLibraryModal';
import { useUnpublishedSummary } from '../../hooks/useUnpublishedSummary';

// Check if we're in mimic mode (for layout offset)
const useIsMimicking = () => {
  const [isMimicking, setIsMimicking] = React.useState(false);

  useEffect(() => {
    const checkMimicState = () => {
      const originalToken = localStorage.getItem('originalAdminToken');
      const mimickedUserData = localStorage.getItem('mimickedUser');
      setIsMimicking(!!originalToken && !!mimickedUserData);
    };

    checkMimicState();
    window.addEventListener('storage', checkMimicState);
    return () => window.removeEventListener('storage', checkMimicState);
  }, []);

  return isMimicking;
};

export const BlogManager: React.FC = () => {
  const { siteId } = useParams<{ siteId: string }>();
  const navigate = useNavigate();
  const isMimicking = useIsMimicking();
  const state = useBlogManagerState();
  const { blogPostStatusMap, refetch: refetchUnpublishedSummary } = useUnpublishedSummary(siteId);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const moreMenuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target as Node)) {
        setIsMoreMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handlers = createBlogManagerHandlers({
    siteId,
    navigate,
    state,
    onAfterBlogPostMutation: refetchUnpublishedSummary,
  });

  // Load initial data
  useEffect(() => {
    if (!siteId) {
      navigate('/dashboard');
      return;
    }

    handlers.loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, navigate]);

  // Load posts when filters change
  useEffect(() => {
    if (state.blog) {
      handlers.loadPosts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.blog, state.currentPage, state.statusFilter, state.selectedTag]);

  // Sync settings form when blog data loads
  useEffect(() => {
    if (state.blog) {
      state.setSettingsForm({
        title: state.blog.title || '',
        description: state.blog.description || '',
        basePath: state.blog.basePath || '/blog',
        layout: state.blog.layout || 'grid',
        layoutId: state.blog.layoutId ?? null,
        showLayout: state.blog.showLayout !== false,
        postsPerPage: state.blog.postsPerPage || 10,
        showAuthor: state.blog.showAuthor ?? true,
        showDate: state.blog.showDate ?? true,
        showReadingTime: state.blog.showReadingTime ?? true,
        showFeaturedImage: state.blog.showFeaturedImage ?? true,
        showTags: state.blog.showTags ?? true,
      });
      // Sync custom colors
      state.setCustomColors((state.blog as any).customColors || {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.blog]);

  // Filter posts by search query
  const filteredPosts = state.posts.filter(post =>
    post.title.toLowerCase().includes(state.searchQuery.toLowerCase())
  );

  if (state.isLoading) {
    return (
      <div className="page-container flex items-center justify-center">
        <div className="text-center animate-fade-in">
          <div className="w-12 h-12 mx-auto mb-4 spinner"></div>
          <p className="text-gray-600 font-medium">Loading blog...</p>
        </div>
      </div>
    );
  }

  if (!state.site || !state.blog) {
    return (
      <div className="page-container flex items-center justify-center">
        <div className="text-center animate-fade-in">
          <h2 className="text-2xl font-bold text-gray-900">Blog not found</h2>
          <p className="mt-2 text-gray-600">
            The blog you're looking for doesn't exist or is not installed.
          </p>
          <Link to={`/sites/${siteId}`} className="mt-4 btn-primary inline-block">
            Back to Site Manager
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={`page-container ${isMimicking ? 'mt-[52px]' : ''}`}>
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-4 sm:py-6 gap-4 sm:gap-0">
            {/* Left side: Back link and blog info */}
            <div className="flex items-center space-x-3 sm:space-x-4 min-w-0">
              <Link
                to={`/sites/${siteId}`}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors shrink-0"
              >
                <ArrowLeft size={20} />
                <span className="hidden sm:inline">Back to Site Manager</span>
              </Link>
              <div className="h-6 w-px bg-gray-300 hidden sm:block"></div>
              <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-r from-blue-500 to-blue-600 flex items-center justify-center shadow-sm shrink-0">
                  <BookOpen className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                </div>
                <div className="min-w-0">
                  <h1 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">
                    {state.blog.title}
                  </h1>
                  <p className="text-gray-600 text-sm hidden sm:block">{state.site.name}</p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-between sm:justify-end space-x-2 sm:space-x-3">
              {/* View Live Blog - hidden on mobile, in dropdown */}
              <Tooltip
                content={
                  state.site?.isPublished && state.blog?.isPublished && state.site?.subdomain
                    ? 'View Live Blog'
                    : 'Blog and site must be published to view live'
                }
              >
                <button
                  onClick={() => {
                    if (!state.site?.subdomain || !state.blog) return;
                    window.open(
                      `${getPublishedSiteUrl(state.site)}${state.blog.basePath || '/blog'}`,
                      '_blank'
                    );
                  }}
                  disabled={
                    !state.site?.isPublished || !state.blog?.isPublished || !state.site?.subdomain
                  }
                  className={cn(
                    'hidden sm:inline-flex p-2 rounded-lg transition-colors items-center',
                    state.site?.isPublished && state.blog?.isPublished && state.site?.subdomain
                      ? 'text-purple-600 hover:bg-purple-50'
                      : 'text-gray-300 cursor-not-allowed'
                  )}
                >
                  <ExternalLink size={20} />
                </button>
              </Tooltip>

              {/* Toggle Blog Visibility - always visible */}
              <button
                onClick={handlers.handleToggleBlogPublish}
                disabled={state.isTogglingBlogPublish}
                className={cn(
                  'px-2 sm:px-3 py-1.5 text-xs sm:text-sm font-medium rounded-lg transition-colors',
                  state.blog?.isPublished
                    ? 'text-orange-600 bg-orange-50 hover:bg-orange-100'
                    : 'text-green-600 bg-green-50 hover:bg-green-100'
                )}
              >
                {state.isTogglingBlogPublish ? (
                  <div className="w-4 h-4 animate-spin border-2 border-current border-t-transparent rounded-full" />
                ) : state.blog?.isPublished ? (
                  'Unpublish'
                ) : (
                  'Publish'
                )}
              </button>

              {/* Blog Settings - always visible */}
              <Tooltip content="Blog Settings">
                <button
                  onClick={() => state.setShowSettingsPanel(!state.showSettingsPanel)}
                  className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Settings size={20} />
                </button>
              </Tooltip>

              {/* SEO Panel - hidden on mobile, in dropdown */}
              <Tooltip content="SEO">
                <button
                  onClick={() => state.setShowSeoPanel(!state.showSeoPanel)}
                  className="hidden sm:block p-2 text-indigo-600 hover:text-white hover:bg-indigo-600 rounded-lg transition-colors"
                >
                  <TrendingUp size={20} />
                </button>
              </Tooltip>

              {/* Media Library - hidden on mobile, in dropdown */}
              <Tooltip content="Media Library">
                <button
                  onClick={() => state.setShowMediaLibrary(true)}
                  className="hidden sm:block p-2 text-amber-600 hover:text-white hover:bg-amber-600 rounded-lg transition-colors"
                >
                  <FolderOpen size={20} />
                </button>
              </Tooltip>

              {/* More menu - visible only on mobile */}
              <div className="relative sm:hidden" ref={moreMenuRef}>
                <button
                  onClick={() => setIsMoreMenuOpen(!isMoreMenuOpen)}
                  className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  aria-expanded={isMoreMenuOpen}
                  aria-haspopup="true"
                >
                  <MoreHorizontal size={20} />
                </button>

                {isMoreMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                    {/* View Live Blog */}
                    <button
                      onClick={() => {
                        if (!state.site?.subdomain || !state.blog) return;
                        window.open(
                          `${getPublishedSiteUrl(state.site)}${state.blog.basePath || '/blog'}`,
                          '_blank'
                        );
                        setIsMoreMenuOpen(false);
                      }}
                      disabled={
                        !state.site?.isPublished ||
                        !state.blog?.isPublished ||
                        !state.site?.subdomain
                      }
                      className={cn(
                        'w-full px-4 py-2 text-left text-sm flex items-center space-x-3',
                        state.site?.isPublished && state.blog?.isPublished && state.site?.subdomain
                          ? 'text-purple-600 hover:bg-purple-50'
                          : 'text-gray-300 cursor-not-allowed'
                      )}
                    >
                      <ExternalLink size={16} />
                      <span>View Live Blog</span>
                    </button>

                    {/* SEO Panel */}
                    <button
                      onClick={() => {
                        state.setShowSeoPanel(!state.showSeoPanel);
                        setIsMoreMenuOpen(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-3"
                    >
                      <TrendingUp size={16} className="text-indigo-600" />
                      <span>SEO</span>
                    </button>

                    {/* Media Library */}
                    <button
                      onClick={() => {
                        state.setShowMediaLibrary(true);
                        setIsMoreMenuOpen(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-amber-600 hover:bg-amber-50 flex items-center space-x-3"
                    >
                      <FolderOpen size={16} />
                      <span>Media Library</span>
                    </button>
                  </div>
                )}
              </div>

              {/* Create New Post - always visible */}
              <button
                onClick={() => navigate(`/sites/${siteId}/blog/posts/new`)}
                className="btn-primary inline-flex items-center space-x-1 sm:space-x-2 text-sm sm:text-base px-2 sm:px-4"
              >
                <Plus size={18} />
                <span className="hidden sm:inline">New Post</span>
                <span className="sm:hidden">New</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {/* Stats Cards */}
          {state.stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="card p-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <FileText className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">{state.stats.totalPosts}</div>
                    <div className="text-sm text-gray-500">Total Posts</div>
                  </div>
                </div>
              </div>
              <div className="card p-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">
                      {state.stats.publishedPosts}
                    </div>
                    <div className="text-sm text-gray-500">Published</div>
                  </div>
                </div>
              </div>
              <div className="card p-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                    <Edit className="h-5 w-5 text-yellow-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">{state.stats.draftPosts}</div>
                    <div className="text-sm text-gray-500">Drafts</div>
                  </div>
                </div>
              </div>
              <div className="card p-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                    <Eye className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">{state.stats.totalViews}</div>
                    <div className="text-sm text-gray-500">Total Views</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Filters and Search */}
          <div className="card p-4 mb-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              {/* Search */}
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search posts..."
                  value={state.searchQuery}
                  onChange={e => state.setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              {/* Filters */}
              <div className="flex items-center space-x-3">
                {/* Status Filter */}
                <select
                  value={state.statusFilter}
                  onChange={e => {
                    state.setStatusFilter(e.target.value as 'all' | 'published' | 'draft');
                    state.setCurrentPage(1);
                  }}
                  className="px-3 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="all">All Status</option>
                  <option value="published">Published</option>
                  <option value="draft">Draft</option>
                </select>

                {/* Tag Filter */}
                {state.tags.length > 0 && (
                  <select
                    value={state.selectedTag || ''}
                    onChange={e => {
                      state.setSelectedTag(e.target.value || null);
                      state.setCurrentPage(1);
                    }}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">All Tags</option>
                    {state.tags.map(({ tag, count }) => (
                      <option key={tag} value={tag}>
                        {tag} ({count})
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>
          </div>

          {/* Posts List */}
          <BlogPostsList
            siteId={siteId!}
            posts={filteredPosts}
            isLoading={state.isLoadingPosts}
            isTogglingPublish={state.isTogglingPublish}
            currentPage={state.currentPage}
            totalPages={state.totalPages}
            totalPosts={state.totalPosts}
            postsPerPage={handlers.postsPerPage}
            onTogglePublish={handlers.handleTogglePostPublish}
            onDelete={post => {
              state.setPostToDelete(post);
              state.setShowDeleteModal(true);
            }}
            onPageChange={state.setCurrentPage}
            blogPostStatusMap={blogPostStatusMap}
          />

          {/* Tags Section */}
          {state.tags.length > 0 && (
            <div className="mt-8">
              <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
                <Tag className="h-5 w-5 text-gray-400" />
                <span>Tags</span>
              </h2>
              <div className="card p-4">
                <div className="flex flex-wrap gap-2">
                  {state.tags.map(({ tag, count }) => (
                    <button
                      key={tag}
                      onClick={() => {
                        state.setSelectedTag(state.selectedTag === tag ? null : tag);
                        state.setCurrentPage(1);
                      }}
                      className={cn(
                        'inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                        state.selectedTag === tag
                          ? 'bg-primary-100 text-primary-800 border-2 border-primary-300'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      )}
                    >
                      {tag}
                      <span className="ml-1.5 text-xs opacity-70">({count})</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Settings Panel */}
      <BlogSettingsPanel
        isOpen={state.showSettingsPanel}
        onClose={() => state.setShowSettingsPanel(false)}
        siteId={siteId}
        settingsForm={state.settingsForm}
        setSettingsForm={state.setSettingsForm}
        customColors={state.customColors}
        showColorPanel={state.showColorPanel}
        setShowColorPanel={state.setShowColorPanel}
        onSave={handlers.handleSaveSettings}
        onColorChange={handlers.handleColorChange}
        onClearColor={handlers.clearColor}
        onResetAllColors={handlers.resetAllColors}
        isSaving={state.isSavingSettings}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={state.showDeleteModal}
        onClose={() => {
          state.setShowDeleteModal(false);
          state.setPostToDelete(null);
        }}
        onConfirm={handlers.handleDeletePost}
        isLoading={state.isDeletingPost}
        title="Delete Post"
        message={`Are you sure you want to delete "${state.postToDelete?.title}"? This action cannot be undone.`}
      />

      {/* SEO Panel */}
      {state.showSeoPanel && state.blog && (
        <BlogSEOOverviewPanel
          blogId={state.blog.id}
          siteId={siteId!}
          isOpen={state.showSeoPanel}
          onClose={() => state.setShowSeoPanel(false)}
        />
      )}

      {/* Media Library Modal */}
      <MediaLibraryModal
        isOpen={state.showMediaLibrary}
        onClose={() => state.setShowMediaLibrary(false)}
        siteId={siteId}
      />

      {/* Toast Notification */}
      {state.showToast && (
        <div className="fixed top-4 right-4 z-50">
          <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-4 max-w-sm animate-slide-in-right">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <div
                  className={cn(
                    'w-8 h-8 rounded-full flex items-center justify-center',
                    state.toastType === 'success' ? 'bg-green-100' : 'bg-red-100'
                  )}
                >
                  {state.toastType === 'success' ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-red-600" />
                  )}
                </div>
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">{state.toastMessage}</p>
              </div>
              <button
                onClick={() => state.setShowToast(false)}
                className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogManager;
